// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// localforage
import * as localforage from 'localforage';

const API_BASE = '/api';

interface LoginData {
  username: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  public login(data: LoginData): Promise<any> {
    try {
      const headers = new HttpHeaders().set('Content-Type', 'application/json');
      const config = { headers };
      return this.http.post(`${API_BASE}/login`, data, config).toPromise();
    } catch (error) {
      console.error('Error from ApiService.login(): ', error);
    }
  }

  public getAgentData(token: string): Promise<any> {
    try {
      return this.http.get(`${API_BASE}/agent?token=${token}`).toPromise();
    } catch (error) {
      console.error('Error from ApiService.getAgentData(): ', error);
    }
  }

  public getSurveySubmissions(token: string, openHouse: any): Promise<any> {
    try {
      return this.http.get(`${API_BASE}/surveySubmissions?token=${token}&open_id=${openHouse.open_id}`).toPromise();
    } catch (error) {
      console.error('Error from ApiService.getSurveySubmissions(): ', error);
    }
  }

  public postSurvey(token: string, data: any): Promise<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const config = { headers };
    const formattedData = { token, data };

    try {
      return this.http.post(`${API_BASE}/survey`, formattedData, config).toPromise();
    } catch (error) {
      console.error('Error from ApiService.postSurvey(): ', error);
    }
  }

  public importGuest(token: string, data: any): Promise<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const config = { headers };
    const formattedData = { token, data };

    try {
      return this.http.post(`${API_BASE}/importGuest`, formattedData, config).toPromise();
    } catch (error) {
      console.error('Error from ApiService.importGuest(): ', error);
    }
  }

  public postFeedback(token: string, data: any): Promise<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const config = { headers };
    const formattedData = { token, data };

    try {
      return this.http.post(`${API_BASE}/feedback`, formattedData, config).toPromise();
    } catch (error) {
      console.error('Error from ApiService.postFeedback(): ', error);
    }
  }

  public async saveTokenToDatabase(token: string): Promise<any> {
    try {
      const response = await localforage.setItem('oh-token', token);
      return response;
    } catch (error) {
      console.error('Error from ApiService.saveTokenToDatabase(): ', error);
    }
  }

  public async getTokenFromDatabase(): Promise<any> {
    try {
      const response: any = await localforage.getItem('oh-token');
      return response;
    } catch (error) {
      console.error('Error from ApiService.getTokenFromDatabase(): ', error);
    }
  }

  public async saveAgentDataToDatabase(data: any) {
    if (data && data.status === 'fail') {
      return;
    }

    try {
      const response = await localforage.setItem('oh-agent-data', data);
      return response;
    } catch (error) {
      console.error('Error from ApiService.saveAgentDataToDatabase(): ', error);
    }
  }

  public async getAgentDataFromDatabase(): Promise<any> {
    try {
      const response: any = await localforage.getItem('oh-agent-data');

      if (response && response.status === 'fail') {
        throw new Error('Error from ApiService.getAgentDataFromDatabase(): no data available');
      }

      return response;
    } catch (error) {
      console.error('Error from ApiService.getAgentDataFromDatabase(): ', error);
    }
  }

  public async logSignInCount(openHouse: any): Promise<any> {
    const key = `oh-sign-in-count-${openHouse.open_id}`;
    let count: any = await localforage.getItem(key);
    count = count ? count : 0;
    await localforage.setItem(key, count + 1);
  }

  public async getLogSignInCount(openHouse: any): Promise<number> {
    if (!openHouse) {
      return null;
    }
    const key = `oh-sign-in-count-${openHouse.open_id}`;
    let count: any = await localforage.getItem(key);
    count = count ? count : 0;
    return count;
  }

  public clearDatabase(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        await localforage.removeItem('oh-agent-data');
        await localforage.removeItem('oh-selected-open-house');
        await localforage.removeItem('oh-token');
        resolve();
      } catch (error) {
        console.error('Error from ApiService.clearDatabase(): ', error);
        reject(error);
      }
    });
  }
}
